import type { IManagementReportDataset } from '@commenda/api';
import { EmptyState } from '@commenda/kakashi';
import { useRef } from 'react';

import { ReportCanvas } from './components/ReportCanvas';
import { ReportProvider } from './components/ReportProvider';
import { ReportToolbar } from './components/ReportToolbar';

export interface IManagementReportProps {
  dataset: IManagementReportDataset;
  /** ISO 4217 currency the statements are reported in. */
  currency: string;
  /** Column headers, breakdown lists and clean chart defaults (MANAGEMENT_REPORT_COLUMN_LAYOUT). */
  columnLayout?: boolean;
  /** Period override via card menu submenus instead of the dialog (MANAGEMENT_REPORT_INLINE_OVERRIDE). */
  inlineOverride?: boolean;
}

const ReportSurface = () => {
  const canvasRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col gap-3xl">
      <ReportToolbar canvasRef={canvasRef} />
      <div className="min-h-[480px]">
        <ReportCanvas ref={canvasRef} />
      </div>
    </div>
  );
};

/** Management Reporting tab: closed-books MIS built from RootFi statements. */
export const ManagementReport = ({
  dataset,
  currency,
  columnLayout = false,
  inlineOverride = false,
}: IManagementReportProps) => {
  const { booksClosedThrough } = dataset;
  const hasClosedMonths =
    booksClosedThrough !== null &&
    dataset.months.some((m) => m.month <= booksClosedThrough);

  if (!hasClosedMonths) {
    return (
      <EmptyState
        heading="No closed books yet"
        description="Management reports only include months whose bookkeeping has been completed. Figures will appear once the first period is closed."
      />
    );
  }

  return (
    <ReportProvider
      dataset={dataset}
      currency={currency}
      booksClosedThrough={booksClosedThrough}
      columnLayout={columnLayout}
      inlineOverride={inlineOverride}
    >
      <ReportSurface />
    </ReportProvider>
  );
};
